import React from 'react';
import { Card, CardBody, Row, Col, Badge, Progress, Spinner } from 'reactstrap';
import { Icon } from '../../../../components/Component'; // Assuming path is correct
import { formatCurrency } from '../../../../utils/Utils'; // Assuming path is correct

/**
 * Formats time in milliseconds to a human-readable format
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} - Formatted time string
 */
const formatTimeToFirstAction = (milliseconds) => {
  // Added check for undefined/null for robustness
  if (milliseconds === undefined || milliseconds === null || isNaN(milliseconds)) return 'N/A';
  if (milliseconds === 0) return '0 minutes';


  const hours = milliseconds / (1000 * 60 * 60);

  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (hours < 24) {
    const roundedHours = Math.round(hours * 10) / 10;
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  } else {
    const days = Math.round(hours / 24 * 10) / 10;
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
};

/**
 * Performance Breakdown Component
 * Displays detailed performance metrics for an agent
 */
const PerformanceBreakdown = ({ data = {}, isLoading = false }) => {
  // Targets for progress - consider making these dynamic or configurable
  const dealsTarget = data?.targets?.dealsCompleted || 10;
  const commissionTarget = data?.targets?.commissionValue || 50000;
  const leadActionTarget = data?.targets?.firstActionCount || 20;
  const listingsTarget = data?.targets?.listingsAcquired || 10;
  const totalPointsTarget = data?.targets?.totalPoints || 500;

  // Calculate percentages for progress bars
  const dealsCompletedPercentage = Math.min(100, ((data?.dealsCompleted || 0) / dealsTarget) * 100);
  const commissionPercentage = Math.min(100, ((data?.commissionValue || 0) / commissionTarget) * 100);
  const leadActionPercentage = Math.min(100, ((data?.firstActionCount || 0) / leadActionTarget) * 100);
  const listingsPercentage = Math.min(100, ((data?.listingsAcquired || 0) / listingsTarget) * 100);
  const totalPointsPercentage = Math.min(100, ((data?.totalPoints || 0) / totalPointsTarget) * 100);

  const iconContainerStyle = { width: '32px', height: '32px', fontSize: '0.9rem' }; // For smaller icons in this context

  return (
    <Card className="shadow-sm border-0"> {/* Removed mb-4, can be added by parent if needed */}
      <CardBody className="p-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <h5 className="card-title mb-2 mb-sm-0">Performance Breakdown</h5>
          <Badge color="primary" pill className="px-3 py-2 fs-caption"> {/* fs-caption for potentially smaller text */}
            {data?.period?.charAt(0).toUpperCase() + data?.period?.slice(1) || 'Monthly'} Performance
          </Badge>
        </div>

        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          </div>
        ) : (
          <>
            <Row className="g-4">
              {/* Deals Completed */}
              <Col md="6">
                <div className="mb-3"> {/* Replaced .performance-metric */}
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle bg-primary bg-opacity-10 me-2 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={iconContainerStyle}
                      >
                        <Icon name="briefcase" className="text-primary" />
                      </div>
                      <h6 className="mb-0 fw-semibold">Deals Completed</h6>
                    </div>
                    <h5 className="mb-0 text-primary fw-bold">{data?.dealsCompleted || 0}</h5>
                  </div>
                  <Progress value={dealsCompletedPercentage} className="mb-1" style={{ height: '6px' }} />
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Total Value:</span>
                    <span className="fw-semibold small">{formatCurrency(data?.dealValue || 0)}</span>
                  </div>
                </div>
              </Col>

              {/* Commission Earned */}
              <Col md="6">
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle bg-success bg-opacity-10 me-2 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={iconContainerStyle}
                      >
                        <Icon name="money" className="text-success" /> {/* Changed icon to 'money' for variety */}
                      </div>
                      <h6 className="mb-0 fw-semibold">Commission Earned</h6>
                    </div>
                    <h5 className="mb-0 text-success fw-bold">{formatCurrency(data?.commissionValue || 0)}</h5>
                  </div>
                  <Progress value={commissionPercentage} className="mb-1" color="success" style={{ height: '6px' }} />
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Per Deal Avg:</span>
                    <span className="fw-semibold small">
                      {formatCurrency(data?.dealsCompleted ? (data.commissionValue || 0) / data.dealsCompleted : 0)}
                    </span>
                  </div>
                </div>
              </Col>

              {/* Lead Actions */}
              <Col md="6">
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle bg-info bg-opacity-10 me-2 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={iconContainerStyle}
                      >
                        <Icon name="users" className="text-info" />
                      </div>
                      <h6 className="mb-0 fw-semibold">Lead Actions</h6>
                    </div>
                    <h5 className="mb-0 text-info fw-bold">{data?.firstActionCount || 0}</h5>
                  </div>
                  <Progress value={leadActionPercentage} className="mb-1" color="info" style={{ height: '6px' }} />
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Avg Time to Action:</span>
                    <span className="fw-semibold small">{formatTimeToFirstAction(data?.avgTimeToFirstAction)}</span>
                  </div>
                </div>
              </Col>

              {/* Listings Acquired */}
              <Col md="6">
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle bg-warning bg-opacity-25 me-2 d-flex align-items-center justify-content-center flex-shrink-0" // Using bg-opacity-25 for warning for better visibility
                        style={iconContainerStyle}
                      >
                        <Icon name="home-alt" className="text-warning" /> {/* Changed icon to 'home-alt' */}
                      </div>
                      <h6 className="mb-0 fw-semibold">Listings Acquired</h6>
                    </div>
                    <h5 className="mb-0 text-warning fw-bold">{data?.listingsAcquired || 0}</h5>
                  </div>
                  <Progress value={listingsPercentage} className="mb-1" color="warning" style={{ height: '6px' }} />
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Conversion Rate:</span>
                    <span className="fw-semibold small">
                      {(data?.firstActionCount && data?.listingsAcquired) ?
                        `${Math.round(((data.listingsAcquired || 0) / data.firstActionCount) * 100)}%` :
                        '0%'}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Total Points */}
            <div className="mt-4 pt-4 border-top">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 fw-bold text-dark">Total Performance Score</h6>
                <h5 className="mb-0 text-primary fw-bolder">{data?.totalPoints || 0} <span className="small text-muted">points</span></h5>
              </div>
              <Progress
                value={totalPointsPercentage}
                className="progress-lg" // Using a class for potentially larger overall progress bar
                color="primary"
                style={{ height: '10px' }}
              />
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default PerformanceBreakdown;